import { signedApiClient } from './apiClient';
import type { PeopleQuery, StudyPeoplePage } from '@/models/studyPeople';
import { studyMemberFromBackend, type NetworkStudyMembers, type StudyMember } from '@/models/studyMember';
import { serializeGrant, type Grant } from '@/models/grant';
import { studyFormTitleFromBackend, type NetworkStudyFormTitles, type StudyFormTitle } from '@/models/studyFormTitle';

/** One member Save, as `PATCH study/:id/user/:userId/permissions` takes it (spdata-api's `MemberPermissionsBody`). */
export interface MemberPermissionsSave {
  grant: Grant;
  /** A person setting, beside the grant rather than in it (users decision 38). Left out, it stays as stored; `null` clears it. */
  notificationProfile?: string | null;
}

/** The groups a member joins and leaves, as `PATCH study/:id/user/:userId/group` takes them (spdata-api's `MemberGroupsDto`). */
export interface MemberGroupsChange {
  add: string[];
  remove: string[];
}

/**
 * One case-team role a person holds in a study, as `GET study/:id/user/:userId/case-roles` sends it
 * (spdata-api's `StudyUserCaseRoles`): the role's key from `flags.caseTeam.roles`, and how many of the
 * study's cases list them under it, at least one.
 */
export interface MemberCaseRole {
  roleKey: string;
  caseCount: number;
}

class StudyUsersService {

  private investigationEndpoint: string = '';
  private studyEndpoint: string = '';

  initService(
    investigationEndpoint: string,
    studyEndpoint: string
  ) {
    this.investigationEndpoint = investigationEndpoint;
    this.studyEndpoint = studyEndpoint;
  }

  /**
   * Every current member of the study, for pickers that need the whole list. Any member may read it,
   * and it is not paged.
   *
   * `getStudyPeople` cannot serve this: it is the People list's read, so it returns one page,
   * searches server-side, and needs Manage users.
   */
  async getStudyMembers(identifier: string): Promise<StudyMember[]> {
    const uri = `${this.studyEndpoint}/${identifier}/members`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkResponse: NetworkStudyMembers = endpointResponse.data;
    return networkResponse.members.map(studyMemberFromBackend);
  }

  /**
   * One page of the People list: members and open invites together, ordered, filtered, counted and
   * paged by the server (users decisions 110 to 112). Needs Manage users.
   */
  async getStudyPeople(identifier: string, query: PeopleQuery): Promise<StudyPeoplePage> {
    let uri = `${this.studyEndpoint}/${identifier}/people?status=${query.status}&page=${query.page}&limit=${query.limit}`;
    if (query.search !== '') {
      uri += `&search=${encodeURIComponent(query.search)}`;
    }
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    return endpointResponse.data;
  }

  /**
   * Every form of the study by id and title, its own and shared ones, in the order the forms list
   * uses, and no definition. Needs Manage users, not Manage forms: it is what a Manage users holder
   * picks from in the By-form lens and a grant's form lists (ENG-10136).
   */
  async getFormTitles(identifier: string): Promise<StudyFormTitle[]> {
    const uri = `${this.studyEndpoint}/${identifier}/form/titles`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    const networkResponse: NetworkStudyFormTitles = endpointResponse.data;
    return networkResponse.forms.map(studyFormTitleFromBackend);
  }

  /**
   * Saves a member's grant in this study. The study comes from the URL, and the server refuses the
   * caller's own id and, from a caller without Manage study, any change to Manage study (users
   * decisions 5, 6 and 13).
   */
  async patchMemberPermissions(identifier: string, userIdentifier: string, save: MemberPermissionsSave) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}/permissions`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, {
      grant: serializeGrant(save.grant),
      ...(save.notificationProfile !== undefined && { notificationProfile: save.notificationProfile })
    });
  }

  /**
   * Puts a member into groups and takes them out of others, apart from their grant (users decision 35).
   * The server refuses the caller's own groups, a former member, and, from a caller without Manage
   * study, any group that grants it (decisions 5, 6, 63 and 65).
   */
  async patchMemberGroups(identifier: string, userIdentifier: string, change: MemberGroupsChange) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}/group`;
    const apiClient = signedApiClient('PATCH', uri);
    return await apiClient.patch(uri, { add: [...change.add], remove: [...change.remove] });
  }

  /**
   * How many of the study's cases list a person under each case-team role, for the remove-member
   * confirmation (users decision 108). Their roled case-team rows outlive a removal (decision 100).
   * Needs Manage users.
   */
  async getMemberCaseRoles(identifier: string, userIdentifier: string): Promise<MemberCaseRole[]> {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}/case-roles`;
    const apiClient = signedApiClient('GET', uri);
    const endpointResponse = await apiClient.get(uri);
    return endpointResponse.data.roles;
  }

  /**
   * Removes a member from the study. The server refuses the caller's own id with 403, someone who is
   * not a member with 404, and the last Manage study holder with 409 (users decisions 91, 92 and 99).
   */
  async removeUserFromStudy(identifier: string, userIdentifier: string) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}`;
    const apiClient = signedApiClient('DELETE', uri);
    return await apiClient.delete(uri);
  }

  /**
   * Deactivates a member: their access ends and their own grant and groups are kept (users decisions
   * 129 and 131). The server refuses as removal does: 403 for the caller's own id, 404 for someone
   * who is not a member, and 409 for the last Manage study holder (decision 132).
   */
  async deactivateMember(identifier: string, userIdentifier: string) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}/deactivate`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri);
  }

  /** Activates a deactivated member, putting their kept grant and groups back in effect (decision 130). */
  async activateMember(identifier: string, userIdentifier: string) {
    const uri = `${this.studyEndpoint}/${identifier}/user/${userIdentifier}/activate`;
    const apiClient = signedApiClient('POST', uri);
    return await apiClient.post(uri);
  }
}

export default new StudyUsersService();
